// ============================================================
// Persistence bridge — now backed by NORMALIZED Supabase tables.
//
//   WRITE: the engine's whole-project snapshot is sent to the
//          replace_project_state(p_project, snap) RPC, which fans it
//          out into records / record_items / table_status / rfis /
//          itp_items / tdd_docs / project_meta transactionally.
//   READ:  on boot we SELECT those tables and reconstruct the exact
//          snapshot the engine's loadFromStorage() expects, staging
//          it into localStorage so the engine consumes it normally.
//
// The data now lives in real relational tables (see
// supabase/migrations/0002_relational.sql). The snapshot is just the
// transport format between the verbatim engine and the tables; the
// component split will read these tables through typed selectors.
// ============================================================
import { supabase, OFFLINE_ONLY } from "./supabase";
import { enqueueSnapshot } from "./offlineQueue";

const LS_KEY = "tt-ballyroe-data";

// The Ballyroe demo project — the default home, and the only project the anon
// key can reach. Authenticated users may switch to any project they belong to.
export const DEMO_PROJECT_ID = "00000000-0000-0000-0000-0000000ba110";
const LS_CURRENT = "gp-current-project";

// The active project id. Persisted so a reload returns you to the same project.
let currentProjectId: string = (() => {
  try {
    return localStorage.getItem(LS_CURRENT) || DEMO_PROJECT_ID;
  } catch {
    return DEMO_PROJECT_ID;
  }
})();

/** The project all reads/writes are currently scoped to. */
export function getProjectId(): string {
  return currentProjectId;
}

/** Switch the active project id (persisted). Callers must re-sync + reload. */
export function setProjectId(id: string): void {
  currentProjectId = id;
  try {
    localStorage.setItem(LS_CURRENT, id);
  } catch {
    /* ignore */
  }
}

// Back-compat alias for the old hardcoded constant.
export const PROJECT_ID = DEMO_PROJECT_ID;

interface Snapshot {
  ts?: number;
  [k: string]: unknown;
}

function localSnapshot(): Snapshot | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? (JSON.parse(raw) as Snapshot) : null;
  } catch {
    return null;
  }
}

// Numeric suffix of a "tdd-12" style id, for stable ordering.
function idNum(id: string): number {
  const m = /(\d+)\s*$/.exec(id || "");
  return m ? parseInt(m[1], 10) : 0;
}

type Row = Record<string, any>;

/** Rebuild the engine snapshot from the relational rows. */
function reconstruct(
  records: Row[],
  items: Row[],
  tableStatusRows: Row[],
  rfiRows: Row[],
  itpRows: Row[],
  tddRows: Row[],
  meta: Row,
): Snapshot {
  const inspections: Record<string, any> = {};
  const stationInspections: Record<string, any> = {};
  const siteInspection: any = { items: {}, comments: [], photos: [] };

  const ensureInv = (st: string, inv: string) => {
    const byStation = (stationInspections[st] ||= {});
    return (byStation[inv] ||= { items: {}, comments: [], photos: [] });
  };

  for (const r of records) {
    const key: string = r.record_key;
    if (r.scope === "station") {
      const [st, inv] = key.split("::INV");
      const rec = ensureInv(st, inv);
      rec.comments = r.comments || [];
      rec.photos = r.photos || [];
    } else if (r.scope === "site") {
      siteInspection.comments = r.comments || [];
      siteInspection.photos = r.photos || [];
    } else {
      const rec: any = { items: {}, comments: r.comments || [], photos: r.photos || [] };
      if (r.status != null) rec.status = r.status;
      if (r.by_name != null) rec.by = r.by_name;
      if (r.at_text != null) rec.at = r.at_text;
      inspections[key] = rec;
    }
  }

  for (const it of items) {
    const item: any = {
      status: it.status,
      note: it.note || "",
      photo: !!it.photo,
      by: it.by_name || "",
      at: it.at_text || "",
      ncr: it.ncr || "",
      comments: it.comments || [],
      photos: it.photos || [],
    };
    if (it.resolved) {
      item.resolved = true;
      item.resolvedBy = it.resolved_by || "";
      item.resolvedAt = it.resolved_at || "";
    }
    const key: string = it.record_key;
    if (key.includes("::INV")) {
      const [st, inv] = key.split("::INV");
      ensureInv(st, inv).items[it.item_idx] = item;
    } else if (key === "SITE") {
      siteInspection.items[it.item_idx] = item;
    } else {
      (inspections[key] ||= { items: {}, comments: [], photos: [] }).items[it.item_idx] = item;
    }
  }

  // Derive the next NCR sequence from the highest NCR already stored, so a
  // cold reload never re-issues a number that's already on a saved failure
  // (the bug behind duplicate NCR-BAL-### across tables/piles). Authoritative
  // over any persisted counter and correct under concurrent inspectors.
  let maxNcr = 15; // seed already uses NCR-BAL-014/015 → next free is 016
  for (const it of items) {
    const m = /NCR-BAL-0*(\d+)/.exec(it.ncr || "");
    if (m) maxNcr = Math.max(maxNcr, parseInt(m[1], 10));
  }

  const tableStatus: Record<string, string> = {};
  for (const t of tableStatusRows) tableStatus[t.table_key] = t.status;

  const rfis: Record<string, any> = {};
  for (const r of rfiRows) {
    rfis[r.id] = {
      id: r.id,
      scope: r.scope,
      target: r.target ?? null,
      title: r.title || "",
      notes: r.notes || "",
      requestedBy: r.requested_by || "",
      requestedAt: r.requested_at,
      requestedFor: r.requested_for,
      status: r.status,
      assignedTo: r.assigned_to,
      scheduledFor: r.scheduled_for,
      closedAt: r.closed_at,
      outcome: r.outcome,
    };
  }

  const itp: { table: any[]; station: any[]; site: any[] } = { table: [], station: [], site: [] };
  [...itpRows]
    .sort((a, b) => a.idx - b.idx)
    .forEach((r) => {
      const entry: any = { cat: r.cat, code: r.code, item: r.item, crit: r.crit };
      if (r.scope === "table" && r.item_scope) entry.scope = r.item_scope;
      (itp as any)[r.scope].push(entry);
    });

  const tddDocs = [...tddRows]
    .sort((a, b) => idNum(a.id) - idNum(b.id))
    .map((r) => ({
      id: r.id,
      cat: r.cat,
      name: r.name,
      ref: r.ref,
      rev: r.rev,
      date: r.doc_date,
      status: r.status,
      by: r.by_name,
      notes: r.notes,
    }));

  return {
    inspections,
    stationInspections,
    siteInspection,
    tableStatus,
    rfis,
    rfiSeq: meta?.rfi_seq ?? 4,
    ncrSeq: maxNcr + 1,
    tddDocs,
    itp,
    currentRole: meta?.cur_role ?? "inspector",
    isLoggedIn: false,
    theme: meta?.theme ?? "dark",
    ts: meta?.updated_at ? Date.parse(meta.updated_at) : 0,
  };
}

/** Fetch + reconstruct a project's full snapshot from the relational tables.
 *  Returns null when the project has no saved state yet (brand-new project). */
async function fetchRemoteSnapshot(pid: string): Promise<Snapshot | null> {
  if (!supabase || OFFLINE_ONLY) return null;
  const { data: meta } = await supabase
    .from("project_meta")
    .select("rfi_seq, cur_role, theme, updated_at")
    .eq("project_id", pid)
    .maybeSingle();
  if (!meta) return null;

  const [records, items, tableStatus, rfis, itp, tdd] = await Promise.all([
    supabase.from("records").select("*").eq("project_id", pid),
    supabase.from("record_items").select("*").eq("project_id", pid).limit(20000),
    supabase.from("table_status").select("*").eq("project_id", pid),
    supabase.from("rfis").select("*").eq("project_id", pid),
    supabase.from("itp_items").select("*").eq("project_id", pid),
    supabase.from("tdd_docs").select("*").eq("project_id", pid),
  ]);

  return reconstruct(
    records.data || [],
    items.data || [],
    tableStatus.data || [],
    rfis.data || [],
    itp.data || [],
    tdd.data || [],
    meta,
  );
}

/** Pull the relational state and, if newer than local, stage it into
 *  localStorage so the engine's own loadFromStorage() consumes it. */
export async function syncRemoteIntoLocal(): Promise<void> {
  if (!supabase || OFFLINE_ONLY) return;
  try {
    const remote = await fetchRemoteSnapshot(currentProjectId);
    if (!remote) return; // nothing saved remotely yet — use local seed
    const local = localSnapshot();
    const remoteTs = Number(remote.ts ?? 0);
    const localTs = Number(local?.ts ?? 0);
    if (!local || remoteTs > localTs) {
      localStorage.setItem(LS_KEY, JSON.stringify(remote));
    }
  } catch {
    /* offline / network — fall back to local silently */
  }
}

/** Hard-replace the engine's localStorage buffer with the active project's
 *  remote state (or a clean slate for a brand-new project). Unlike
 *  syncRemoteIntoLocal this ignores timestamps — it is the authoritative load
 *  when SWITCHING projects, where the local buffer belongs to the old one. */
export async function loadProjectIntoEngine(): Promise<void> {
  let snap: Snapshot | null = null;
  try {
    snap = await fetchRemoteSnapshot(currentProjectId);
  } catch {
    snap = null;
  }
  // Empty project → reset the engine to a clean slate rather than leaking the
  // previously-loaded project's inspections.
  const clean =
    snap ??
    reconstruct([], [], [], [], [], [], { rfi_seq: 4, cur_role: "inspector", theme: "dark" });
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(clean));
  } catch {
    /* ignore */
  }
}

let _timer: ReturnType<typeof setTimeout> | null = null;
let _pending: Snapshot | null = null;

async function flush(): Promise<void> {
  _timer = null;
  const snap = _pending;
  _pending = null;
  if (!supabase || OFFLINE_ONLY || !snap) return;
  const pid = currentProjectId;
  try {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      throw new Error("offline");
    }
    await supabase.rpc("replace_project_state", { p_project: pid, snap });
  } catch {
    // Offline or transient failure — hand the snapshot to the durable queue,
    // which retries on reconnect (Task 5). localStorage stays authoritative.
    try {
      await enqueueSnapshot(pid, snap);
    } catch {
      /* last-resort: localStorage already holds the state */
    }
  }
}

/** Install the engine → Supabase push hook (debounced 1.2s). */
export function installPersistHook(): void {
  if (!supabase || OFFLINE_ONLY) return;
  window.__gpPersist = (snapshot: unknown) => {
    _pending = snapshot as Snapshot;
    if (_timer) clearTimeout(_timer);
    _timer = setTimeout(flush, 1200);
  };
}
